import React, { useState, useRef, useEffect } from "react";
import getApiUrl from "../utils/api";
import { gsap } from "gsap";
import "./AddGroupModal.css";

const AddGroupModal = ({
  open,
  onClose,
  onAddGroup,
  onSubmit,
  initialData,
}) => {
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [members, setMembers] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [inviteEmails, setInviteEmails] = useState(""); // For external invites
  const modalRef = useRef(null);

  useEffect(() => {
    if (open && modalRef.current) {
      gsap.fromTo(
        modalRef.current,
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" }
      );
    }
  }, [open]);

  // Fetch all users when modal opens for the members multi-select
  useEffect(() => {
    if (!open) return;
    const fetchEmployees = async () => {
      setLoadingUsers(true);
      setUserError("");
      try {
        const res = await fetch(getApiUrl("api/auth/employees"), {
          credentials: "include",
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          const opts = data.map((u) => ({
            value: String(u.id),
            label:
              `${u.FirstName || ""} ${u.LastName || ""}`.trim() +
              (u.email ? ` (${u.email})` : ""),
            email: u.email,
            department: u.department_id,
          }));
          setEmployeeOptions(opts);
        } else {
          setEmployeeOptions([]);
        }
      } catch (e) {
        setUserError("Failed to load users");
        setEmployeeOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchEmployees();
  }, [open]);

  // Pre-fill fields when editing
  useEffect(() => {
    if (open && initialData) {
      setGroupName(initialData.groupName || "");
      setDescription(initialData.description || "");
      setMembers(
        Array.isArray(initialData.members)
          ? initialData.members.map(String)
          : []
      );
    } else if (open && !initialData) {
      setGroupName("");
      setDescription("");
      setMembers([]);
      setInviteEmails("");
    }
  }, [open, initialData]);

  // Filter employees based on search query
  const filteredEmployees = employeeOptions.filter(
    (emp) =>
      emp.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddMember = (memberId) => {
    if (!members.includes(memberId)) {
      setMembers((prev) => [...prev, memberId]);
    }
  };

  const handleRemoveMember = (memberId) => {
    setMembers((prev) => prev.filter((id) => id !== memberId));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!groupName || !description || members.length === 0) return;

    const memberLabels = employeeOptions
      .filter((opt) => members.includes(opt.value))
      .map((opt) => opt.label);

    const payload = {
      groupName,
      description,
      members,
      memberLabels,
      inviteEmails: inviteEmails
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean),
    };

    if (onSubmit) onSubmit(payload);
    else if (onAddGroup) onAddGroup(payload);

    // Reset form
    setGroupName("");
    setDescription("");
    setMembers([]);
    setInviteEmails("");
    setSearchQuery("");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="add-group-overlay">
      <div ref={modalRef} className="add-group-modal">
        {/* Header */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
            color: "#374151",
            transition: "all 0.2s ease",
            borderRadius: "50%",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.color = "#6c757d";
            e.currentTarget.style.background = "rgba(255, 255, 255, 1)";
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = "#374151";
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
          }}
        >
          ×
        </button>

        <div
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #868e96 100%)",
            padding: "32px 32px 24px 32px",
            color: "white",
            textAlign: "center",
            position: "relative",
          }}
        >
          <h2
            style={{
              textAlign: "center",
              margin: 0,
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "0.02em",
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {initialData ? "Edit Group" : "Create New Group"}
          </h2>
          <div
            style={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.9)",
              marginBottom: 0,
              marginTop: 8,
              fontSize: "0.9rem",
              fontWeight: 400,
            }}
          >
            {initialData
              ? "Update group details and member settings"
              : "Set up a new group with enhanced member management"}
          </div>
        </div>

        <div
          style={{
            padding: "32px",
            background: "#fafbfc",
            maxHeight: "calc(90vh - 120px)",
            overflowY: "auto",
          }}
        >
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div style={{ marginBottom: 24 }}>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  color: "#374151",
                  fontSize: "1.1rem",
                }}
              >
                Basic Information
              </h3>

              <div style={{ marginBottom: 18 }}>
                <label
                  style={{ fontWeight: 500, display: "block", marginBottom: 6 }}
                >
                  Group Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    fontSize: 16,
                    transition: "border-color 0.2s",
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label
                  style={{ fontWeight: 500, display: "block", marginBottom: 6 }}
                >
                  Description <span style={{ color: "red" }}>*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the purpose of this group"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    fontSize: 16,
                    minHeight: 80,
                    resize: "vertical",
                  }}
                  required
                />
              </div>
            </div>

            {/* Member Management */}
            <div style={{ marginBottom: 24 }}>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  color: "#374151",
                  fontSize: "1.1rem",
                }}
              >
                Member Management
              </h3>

              {/* Search and Add Members */}
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{ fontWeight: 500, display: "block", marginBottom: 6 }}
                >
                  Search & Add Members <span style={{ color: "red" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search employees by name or email..."
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: "1px solid #d1d5db",
                      fontSize: 16,
                    }}
                  />
                  {loadingUsers ? (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        maxHeight: 200,
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      }}
                    >
                      <div style={{ padding: "12px", color: "#6b7280" }}>
                        Loading users...
                      </div>
                    </div>
                  ) : userError ? (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "white",
                        border: "1px solid #f87171",
                        borderRadius: 8,
                        padding: "12px",
                        zIndex: 1000,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        color: "#b91c1c",
                      }}
                    >
                      {userError}
                    </div>
                  ) : (
                    searchQuery && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "white",
                          border: "1px solid #d1d5db",
                          borderRadius: 8,
                          maxHeight: 200,
                          overflowY: "auto",
                          zIndex: 1000,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                      >
                        {filteredEmployees
                          .filter((emp) => !members.includes(emp.value))
                          .slice(0, 10)
                          .map((emp) => (
                            <div
                              key={emp.value}
                              onClick={() => handleAddMember(emp.value)}
                              style={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f3f4f6",
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.background = "#f9fafb")
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  background: "#6c757d",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {emp.label.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 500 }}>
                                  {emp.label.split(" (")[0]}
                                </div>
                                {emp.email && (
                                  <div
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {emp.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* External Invites */}
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{ fontWeight: 500, display: "block", marginBottom: 6 }}
                >
                  Invite External Members
                </label>
                <input
                  type="text"
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="Enter email addresses separated by commas"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    fontSize: 16,
                  }}
                />
                <div
                  style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: 4 }}
                >
                  External members will receive email invitations
                </div>
              </div>

              {/* Current Members */}
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{ fontWeight: 500, display: "block", marginBottom: 6 }}
                >
                  Current Members ({members.length})
                </label>
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    background: "white",
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {members.length === 0 ? (
                    <div
                      style={{
                        padding: "16px",
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      No members added yet
                    </div>
                  ) : (
                    members.map((memberId) => {
                      const member = employeeOptions.find(
                        (opt) => opt.value === memberId
                      );
                      if (!member) return null;

                      return (
                        <div
                          key={memberId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            borderBottom: "1px solid #f3f4f6",
                            background: "#f9fafb",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                background: "#6c757d",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "0.8rem",
                              }}
                            >
                              {member.label.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                {member.label.split(" (")[0]}
                              </div>
                              {member.email && (
                                <div
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#6b7280",
                                  }}
                                >
                                  {member.email}
                                </div>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemoveMember(memberId)}
                            style={{
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              cursor: "pointer",
                              fontSize: "0.8rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                width: "100%",
                background: "#5b7fff",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "14px 0",
                fontWeight: 600,
                fontSize: 16,
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#4a6bff")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#5b7fff")}
            >
              {initialData ? "Update Group" : "Create Group"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddGroupModal;
